import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Loader, Text, Stack, Group, LoadingOverlay } from "@mantine/core";
import { IconX, IconCheck } from "@tabler/icons-react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { getEdgeRetrieveUrl } from "../../../utils/edge";
import { DataContext } from "../../../context/DataContext";

const ClearReturn = () => {
  const { settings, data } = useContext(DataContext);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("PENDING");
  const [loading, setLoading] = useState(true);

  // Detect edge mode from state parameter (-edge suffix) or data context
  const stateParam = searchParams.get("state");
  const isEdgeModeFromState = stateParam?.endsWith("-edge");
  const isEdgeMode = data?.edge === true || isEdgeModeFromState;

  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);

      // Extract the actual request_id from state (remove "-edge" suffix if present)
      const actualRequestId = isEdgeModeFromState
        ? stateParam.replace("-edge", "")
        : stateParam;

      if (isEdgeMode) {
        // Edge mode: POST to edge retrieve endpoint
        try {
          const response = await fetch(getEdgeRetrieveUrl(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              request_id: actualRequestId || settings.request_id,
              type: "idmax",
              code: "continue",
            }),
          });

          if (response.ok) {
            const result = await response.json();
            console.log("Edge retrieve response:", result);
            setStatus(result.status || "SUCCESS");
          } else {
            console.error("Edge retrieve error:", response.status);
            setStatus("ERROR");
          }
        } catch (error) {
          console.error("Edge retrieve error:", error);
          setStatus("ERROR");
        }
        setLoading(false);
        return;
      }

      // Normal mode: POST to standard backend
      const requestData = {
        ...data,
        request_id: settings.request_id,
        isIDMaxContinue: true,
        provider: "CLEAR",
        redirect_uri: "https://fe.dcams.app/clear/return"
      };

      const primaryUrl = `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/idmax`;

      const tryFetch = async (url) => {
        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            console.warn(`HTTP error from ${url}: ${response.status}`);
            return null;
          }

          const result = await response.json();

          if (!result || result.data === false) {
            console.warn(`No valid data returned from ${url}`);
            return null;
          }

          return result;
        } catch (error) {
          console.warn(`Failed to fetch from ${url}:`, error);
          return null;
        }
      };

      let data_returned = await tryFetch(primaryUrl);

      if (data_returned) {
        setStatus(data_returned.status);
      } else {
        console.error("Error fetching status from backend");
        setStatus("ERROR");
      }

      setLoading(false);
    };

    fetchStatus();
  }, [searchParams]);

  useEffect(() => {
    // Attempt to close the current tab
    if (status === "PENDING") {
      return;
    }
    window.close();
  }, [status]);

  return <LoadingOverlay visible />;
};

export default ClearReturn;
